import React, { useEffect, useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ────────────────────────────────────────────────────────────────
const getQtyConfig = (qty, min) => {
  if (qty <= 0)  return { color: 'text-red-500',    dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,.6)]', badge: 'bg-red-500/10 border-red-500/30 text-red-500',       label: 'Esaurito'     };
  if (qty < min) return { color: 'text-amber-500',  dot: 'bg-amber-500',                                    badge: 'bg-amber-500/10 border-amber-500/30 text-amber-500', label: 'Sotto scorta' };
  return                 { color: 'text-emerald-500', dot: 'bg-emerald-500',                                 badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500', label: 'Disponibile' };
};

const getStatoConfig = (stato) => {
  const s = stato?.toLowerCase() || '';
  if (s.includes('pronto'))      return { color: 'text-emerald-500', dot: 'bg-emerald-500 animate-pulse', label: 'Pronto',         bg: 'bg-emerald-500/10 border-emerald-500/30' };
  if (s.includes('ordine'))      return { color: 'text-amber-500',   dot: 'bg-amber-500',                 label: 'In Ordine',      bg: 'bg-amber-500/10 border-amber-500/30'   };
  if (s.includes('riparazione')) return { color: 'text-red-500',     dot: 'bg-red-500',                   label: 'In Riparazione', bg: 'bg-red-500/10 border-red-500/30'       };
  return                                 { color: 'text-slate-400',   dot: 'bg-slate-400',                 label: stato || 'N/D',   bg: 'bg-slate-400/10 border-slate-400/30'  };
};

const getToday = () => new Date().toISOString().split('T')[0];

// ─── Sotto-componenti ────────────────────────────────────────────────────────

const Campo = ({ label, value, mono = false, isEditing, onChange, type = 'text', name, disabled = false }) => {
  const displayValue = (type === 'date' && isEditing && !value) ? getToday() : (value ?? '');
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">
        {label}
      </span>
      {isEditing ? (
        <input
          type={type}
          name={name}
          value={displayValue}
          onChange={onChange}
          disabled={disabled}
          className={`bg-background border border-border/60 rounded-xl px-3 py-2 text-sm font-bold outline-none w-full transition-all ${
            disabled
              ? 'opacity-50 cursor-not-allowed bg-muted/20'
              : 'focus:ring-2 focus:ring-primary/20 hover:border-primary/40'
          }`}
        />
      ) : (
        <span className={`text-[15px] font-bold text-foreground/90 break-words ${mono ? 'font-mono tracking-tight' : ''}`}>
          {value ?? '---'}
        </span>
      )}
    </div>
  );
};

const Sezione = ({ titolo, children, icon }) => (
  <div className="flex flex-col gap-5 p-5 md:p-6 rounded-3xl bg-muted/20 border border-border/40 group hover:border-primary/20 transition-colors">
    <div className="flex items-center gap-3">
      {icon && <Icon name={icon} size={14} className="text-primary/60" />}
      <span className="text-[11px] font-black uppercase tracking-[.2em] text-primary/70">{titolo}</span>
      <div className="flex-1 h-px bg-border/20" />
    </div>
    <div className="grid gap-6">{children}</div>
  </div>
);

// ─── Toggle Switch elegante ───────────────────────────────────────────────────
const ToggleSwitch = ({ checked, onChange, label, sublabel }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 w-full text-left ${
      checked
        ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/15'
        : 'bg-muted/30 border-border/40 hover:border-border/60'
    }`}
  >
    {/* Track */}
    <div className={`relative shrink-0 w-10 h-5 rounded-full transition-colors duration-300 ${checked ? 'bg-emerald-500' : 'bg-muted/80 border border-border/50'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-300 ${checked ? 'left-5' : 'left-0.5'}`} />
    </div>
    {/* Labels */}
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className={`text-[11px] font-black uppercase tracking-widest transition-colors ${checked ? 'text-emerald-500' : 'text-muted-foreground/50'}`}>
        {label}
      </span>
      {sublabel && (
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/30">
          {sublabel}
        </span>
      )}
    </div>
    {/* Icona */}
    <div className="ml-auto">
      <Icon
        name={checked ? 'BookMarked' : 'BookDashed'}
        size={14}
        className={`transition-colors ${checked ? 'text-emerald-500' : 'text-muted-foreground/25'}`}
      />
    </div>
  </button>
);

// ─── Componente principale ────────────────────────────────────────────────────

const DettaglioModal = ({ item, onClose, onUpdate }) => {
  const [isEditing, setIsEditing]           = useState(false);
  const [formData, setFormData]             = useState({ ...item });
  const [loading, setLoading]               = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [eanLocked, setEanLocked]           = useState(true);
  const [notification, setNotification]     = useState(null);

  // Toggle "Salva nello storico" — default attivo
  const [salvaNelloStorico, setSalvaNelloStorico] = useState(true);

  // Profilo utente autenticato — salvato quando si clicca Modifica
  const utenteRef = useRef(null);

  // Qty originale al momento dell'apertura del modal — serve per calcolare il delta
  const qtaOriginaleRef = useRef(Number(item.qtapresente) ?? 0);

  if (!item) return null;

  // ─── Escape + blocca scroll body ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // ─── Auto-dismiss notifica ────────────────────────────────────────────────
  useEffect(() => {
    if (notification) {
      const t = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(t);
    }
  }, [notification]);

  // ─── Click su "Modifica" / "Salva" (header) ──────────────────────────────
  const handleEditClick = async () => {
    if (isEditing) { handleSave(); return; }

    setIsCheckingAuth(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setNotification({ type: 'error', message: 'Devi essere loggato per modificare!' });
        return;
      }

      const { data: profil, error: dbError } = await supabase
        .from('profili')
        .select('nome, cognome, ruolo')
        .eq('id', user.id)
        .single();

      if (dbError || !profil) {
        setNotification({ type: 'error', message: 'Errore nel recupero profilo!' });
        return;
      }

      if (profil.ruolo === 'cco-admin' || profil.ruolo === 'Owner') {
        // Salvo il profilo per usarlo in handleSave
        utenteRef.current = profil;
        // Reset note e salvo la qty originale al momento di apertura editing
        qtaOriginaleRef.current = Number(formData.qtapresente) ?? 0;
        setFormData(prev => ({ ...prev, note: '' }));
        setIsEditing(true);
      } else {
        setNotification({
          type: 'error',
          message: `${profil.nome || 'Utente'}, non sei autorizzato a modificare le giacenze!`,
        });
      }
    } catch (err) {
      setNotification({ type: 'error', message: 'Errore imprevisto!' });
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // ─── Elimina articolo ─────────────────────────────────────────────────────
  const handleDelete = async () => {
    const conferma = window.confirm('Sei sicuro di voler eliminare questo articolo?');
    if (!conferma) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('cco_magazzino')
        .delete()
        .eq('ean', item.ean);

      if (error) throw error;

      setNotification({ type: 'success', message: 'Articolo eliminato con successo!' });
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: "Errore durante l'eliminazione: " + err.message });
      setLoading(false);
    }
  };

  // ─── Input change generico ────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ─── +/- quantità ─────────────────────────────────────────────────────────
  const handleQtyChange = (amount) => {
    const currentQty = Number(formData.qtapresente) || 0;
    const newQty = currentQty + amount;
    if (newQty < 0) return;

    let noviStato = formData.stato;
    if (newQty === 0) noviStato = 'In Riparazione';
    else if (newQty >= 1) noviStato = 'Pronto';

    setFormData(prev => ({ ...prev, qtapresente: newQty, stato: noviStato }));
  };

  // ─── Salva modifiche ─────────────────────────────────────────────────────
  const handleSave = async () => {
    // Se il toggle storico è attivo, la nota è obbligatoria
    if (salvaNelloStorico && (!formData.note || formData.note.trim() === '')) {
      setNotification({ type: 'error', message: 'Nota obbligatoria quando "Salva nello storico" è attivo!' });
      return;
    }

    setLoading(true);
    try {
      // ── 1. Controlla EAN duplicato se è stato cambiato ───────────────────
      if (formData.ean !== item.ean) {
        const { data: existing } = await supabase
          .from('cco_magazzino')
          .select('ean')
          .eq('ean', formData.ean)
          .maybeSingle();

        if (existing) {
          setNotification({ type: 'error', message: 'EAN già esistente, prova con un altro!' });
          setLoading(false);
          return;
        }
      }

      // ── 2. Update cco_magazzino ──────────────────────────────────────────
      const { error: updateError } = await supabase
        .from('cco_magazzino')
        .update(formData)
        .eq('ean', item.ean);

      if (updateError) throw updateError;

      // ── 3. Insert cco_storico (solo se toggle attivo) ────────────────────
      if (salvaNelloStorico) {
        const qtaFinale      = Number(formData.qtapresente) ?? 0;
        const qtaMovimentata = qtaFinale - qtaOriginaleRef.current;

        // tipoMovimento: "Caricato +" se aumento, "Scaricato -" se diminuzione, "Rettifica" se uguale
        let tipoMovimento;
        if (qtaMovimentata > 0)      tipoMovimento = `Caricato +`;
        else if (qtaMovimentata < 0) tipoMovimento = `Scaricato -`;
        else                          tipoMovimento = 'Rettifica';

        const utente = utenteRef.current;
        const nomeUtente = utente
          ? `${utente.nome ?? ''} ${utente.cognome ?? ''}`.trim()
          : 'Sconosciuto';

        const rigaStorico = {
          data:             new Date().toISOString(),   // timestamp completo
          ean:              item.ean,                   // EAN originale (prima key)
          nome:             formData.nome ?? item.nome, // nome articolo
          nota:             formData.note ?? '',         // nota inserita dall'utente
          qtamovimentata:   qtaMovimentata,              // delta (negativo se scarico)
          qtapresente:      qtaFinale,                   // quantità finale dopo modifica
          tipomovimento:    tipoMovimento,               // "Caricato +" / "Scaricato -" / "Rettifica"
          utente:           nomeUtente,                  // "Nome Cognome" dal profilo
        };

        const { error: storicoError } = await supabase
          .from('cco_storico')
          .insert([rigaStorico]);

        if (storicoError) {
          // Non blocchiamo il flusso — il magazzino è già salvato
          console.error('Errore inserimento storico:', storicoError.message);
          setNotification({ type: 'error', message: 'Magazzino aggiornato ma errore nel salvataggio storico: ' + storicoError.message });
          if (onUpdate) onUpdate(formData);
          setIsEditing(false);
          setEanLocked(true);
          setLoading(false);
          return;
        }
      }

      // ── 4. Tutto ok ──────────────────────────────────────────────────────
      setNotification({ type: 'success', message: salvaNelloStorico ? 'Dati aggiornati e storico salvato!' : 'Dati aggiornati con successo!' });
      if (onUpdate) onUpdate(formData);
      setIsEditing(false);
      setEanLocked(true);

    } catch (err) {
      setNotification({ type: 'error', message: 'Errore: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  // ─── Computed values ──────────────────────────────────────────────────────
  const stato  = getStatoConfig(formData.stato);
  const qty    = getQtyConfig(formData.qtapresente, formData.minscorta);
  const pctScorta = formData.minscorta > 0
    ? Math.min(100, Math.round((formData.qtapresente / (formData.minscorta * 2)) * 100))
    : formData.qtapresente > 0 ? 100 : 0;
  const coloreBarraScorta = formData.qtapresente <= 0
    ? 'bg-red-500'
    : formData.qtapresente < formData.minscorta
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  // Delta qty per mostrare anteprima nel toggle
  const deltaQty = Number(formData.qtapresente ?? 0) - qtaOriginaleRef.current;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Notifica toast */}
      {notification && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[110] px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 ${
          notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <Icon name={notification.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={20} />
          <span className="text-sm font-bold uppercase tracking-wider">{notification.message}</span>
        </div>
      )}

      {/* Pannello */}
      <div className="relative w-full h-full md:h-auto md:max-h-[85vh] md:max-w-2xl bg-card border-t md:border border-border/60 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300">

        {/* Drag handle mobile */}
        <div className="md:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-12 h-1.5 rounded-full bg-muted/50" />
        </div>

        {/* ── HEADER ── */}
        <div className="shrink-0 px-6 md:px-8 pt-4 pb-6 border-b border-border/40">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${qty.dot}`} />
                <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/60">Dettagli Articolo</span>
              </div>
              {isEditing ? (
                <input
                  name="nome"
                  value={formData.nome ?? ''}
                  onChange={handleInputChange}
                  className="bg-transparent border-b border-primary/30 font-black uppercase tracking-tighter text-foreground leading-[1.1] outline-none text-2xl w-full"
                />
              ) : (
                <h3
                  className="font-black uppercase tracking-tighter text-foreground leading-[1.1] break-words"
                  style={{ fontSize: 'clamp(1.25rem, 5vw, 1.875rem)' }}
                >
                  {formData.nome || 'N/A'}
                </h3>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleEditClick}
                disabled={loading || isCheckingAuth}
                className={`shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center transition-all shadow-sm border ${
                  isEditing
                    ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                    : 'bg-muted/50 text-muted-foreground hover:text-primary border-border/40'
                }`}
              >
                <Icon
                  name={(loading || isCheckingAuth) ? 'Loader2' : isEditing ? 'Check' : 'Edit3'}
                  size={20}
                  className={(loading || isCheckingAuth) ? 'animate-spin' : ''}
                />
              </button>
              <button
                onClick={onClose}
                className="shrink-0 w-10 h-10 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 hover:border-red-500/20 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all shadow-sm"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* ── BODY scrollabile ── */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 scrollbar-hide">

          {/* Badge stato + giacenza */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-5 rounded-3xl border ${stato.bg} flex flex-col gap-2 transition-colors duration-500`}>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/40">Stato Attuale</span>
              {isEditing ? (
                <select
                  name="stato"
                  value={formData.stato ?? ''}
                  onChange={handleInputChange}
                  className="bg-transparent font-black uppercase text-foreground outline-none cursor-pointer w-full"
                >
                  <option value="PRONTO">Pronto</option>
                  <option value="IN ORDINE">In Ordine</option>
                  <option value="IN RIPARAZIONE">In Riparazione</option>
                </select>
              ) : (
                <span className={`text-base font-black uppercase ${stato.color}`}>{stato.label}</span>
              )}
            </div>
            <div className={`p-5 rounded-3xl border ${qty.badge} flex flex-col gap-2`}>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/40">Giacenza</span>
              <span className={`text-base font-black uppercase ${qty.color}`}>{qty.label}</span>
            </div>
          </div>

          {/* Sezione Giacenza */}
          <Sezione titolo="Giacenza & Scorte" icon="Box">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 items-center">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Disponibile</span>
                <div className="flex items-center gap-4">
                  {isEditing && (
                    <button
                      onClick={() => handleQtyChange(-1)}
                      className="w-8 h-8 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                    >
                      <Icon name="Minus" size={16} strokeWidth={3} />
                    </button>
                  )}
                  <span className={`text-5xl font-black tabular-nums ${qty.color}`}>
                    {formData.qtapresente ?? 0}
                  </span>
                  {isEditing && (
                    <button
                      onClick={() => handleQtyChange(1)}
                      className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all"
                    >
                      <Icon name="Plus" size={16} strokeWidth={3} />
                    </button>
                  )}
                </div>
                {/* Anteprima delta — solo in editing se è cambiata la qty */}
                {isEditing && deltaQty !== 0 && (
                  <span className={`text-[10px] font-black mt-1 ${deltaQty > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {deltaQty > 0 ? `+${deltaQty}` : deltaQty} rispetto all'originale ({qtaOriginaleRef.current})
                  </span>
                )}
              </div>
              <Campo label="Scorta Minima" value={formData.minscorta} isEditing={isEditing} name="minscorta" type="number" onChange={handleInputChange} />
              <Campo label="Unità" value="PZ" />
            </div>
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-end">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Salute Magazzino</span>
                <span className="text-xs font-black tabular-nums text-primary">{pctScorta}%</span>
              </div>
              <div className="h-3 rounded-full bg-muted/60 overflow-hidden border border-border/20 p-0.5">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ease-out ${coloreBarraScorta}`}
                  style={{ width: `${pctScorta}%` }}
                />
              </div>
            </div>
          </Sezione>

          {/* Sezione Specifiche Tecniche */}
          <Sezione titolo="Specifiche Tecniche" icon="Cpu">
            <div className="grid grid-cols-2 gap-y-8 gap-x-4">
              <Campo label="EAN Code"      value={formData.ean}  mono isEditing={isEditing} name="ean"  disabled={eanLocked} onChange={handleInputChange} />
              <Campo label="Codice SAP"    value={formData.sap}  mono isEditing={isEditing} name="sap"                       onChange={handleInputChange} />
              <Campo label="MM (Posizione)" value={formData.mm}       isEditing={isEditing} name="mm"                        onChange={handleInputChange} />
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">Status EAN</span>
                {isEditing ? (
                  <button
                    onClick={() => setEanLocked(!eanLocked)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase transition-all ${
                      eanLocked
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-500 hover:bg-amber-500/20'
                        : 'bg-emerald-500 text-white border-emerald-600'
                    }`}
                  >
                    <Icon name={eanLocked ? 'Lock' : 'LockOpen'} size={12} />
                    {eanLocked ? 'Sblocca EAN' : 'EAN Sbloccato'}
                  </button>
                ) : (
                  <span className="text-emerald-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg w-fit">
                    <Icon name="ShieldCheck" size={12} /> Verified
                  </span>
                )}
              </div>
            </div>
          </Sezione>

          {/* Sezione Tempistiche */}
          <Sezione titolo="Tempistiche" icon="Calendar">
            <div className="grid grid-cols-2 gap-6">
              <Campo label="Data Ordine"       value={formData.dataordine}       isEditing={isEditing} name="dataordine"       type="date" onChange={handleInputChange} />
              <Campo label="Data Sostituzione" value={formData.datasostituzione} isEditing={isEditing} name="datasostituzione" type="date" onChange={handleInputChange} />
            </div>
          </Sezione>

          {/* ── Sezione Note + Toggle Storico (solo in editing) ── */}
          <Sezione titolo="Note Aggiuntive" icon="FileText">

            {/* Toggle "Salva nello storico" — visibile solo quando si è in editing */}
            {isEditing && (
              <div className="space-y-2">
                <ToggleSwitch
                  checked={salvaNelloStorico}
                  onChange={setSalvaNelloStorico}
                  label="Salva nello storico"
                  sublabel={salvaNelloStorico ? 'Nota obbligatoria — verrà registrata in cco_storico' : 'La modifica non verrà registrata nello storico'}
                />
                {/* Anteprima del movimento che verrà scritto */}
                {salvaNelloStorico && deltaQty !== 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/5 border border-primary/15">
                    <Icon name="ArrowRightLeft" size={12} className="text-primary/60 shrink-0" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-primary/60">
                      Movimento:&nbsp;
                      <span className={deltaQty > 0 ? 'text-emerald-500' : 'text-red-500'}>
                        {deltaQty > 0 ? `Caricato + (${deltaQty > 0 ? '+' : ''}${deltaQty} pz)` : `Scaricato - (${deltaQty} pz)`}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Campo note */}
            {isEditing ? (
              <div className="space-y-1.5">
                <textarea
                  name="note"
                  value={formData.note ?? ''}
                  onChange={handleInputChange}
                  placeholder={
                    salvaNelloStorico
                      ? 'Inserisci il motivo della modifica (es. Arrivo merce, Sostituzione...)'
                      : 'Nota opzionale...'
                  }
                  className={`w-full bg-background border rounded-2xl p-4 text-sm font-medium outline-none focus:ring-2 focus:ring-primary/20 min-h-[100px] transition-all resize-none ${
                    salvaNelloStorico && (!formData.note || formData.note.trim() === '')
                      ? 'border-red-500/50 focus:ring-red-500/20'
                      : 'border-border/60'
                  }`}
                />
                {/* Label obbligatorio */}
                {salvaNelloStorico && (!formData.note || formData.note.trim() === '') && (
                  <p className="text-[10px] font-black uppercase tracking-widest text-red-500/80 flex items-center gap-1.5">
                    <Icon name="AlertCircle" size={11} />
                    Campo obbligatorio quando lo storico è attivo
                  </p>
                )}
              </div>
            ) : (
              formData.note
                ? <p className="text-sm font-medium text-foreground/70 leading-relaxed italic">"{formData.note}"</p>
                : <p className="text-sm font-medium text-muted-foreground/40 italic uppercase text-[10px] tracking-widest">Nessuna nota presente.</p>
            )}
          </Sezione>

          {/* Campi extra dinamici (metadati) */}
          {(() => {
            const skip = new Set(['id', 'nome', 'ean', 'qtapresente', 'minscorta', 'stato', 'mm', 'note', 'posizione', 'sap', 'dataordine', 'datasostituzione']);
            const extras = Object.entries(formData).filter(([k, v]) => !skip.has(k) && v !== null && v !== '');
            if (extras.length === 0) return null;
            return (
              <Sezione titolo="Metadati" icon="Layers">
                <div className="grid grid-cols-2 gap-6">
                  {extras.map(([k, v]) => (
                    <Campo key={k} label={k.replace(/_/g, ' ')} value={String(v)} isEditing={isEditing} name={k} onChange={handleInputChange} />
                  ))}
                </div>
              </Sezione>
            );
          })()}
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 px-8 py-5 border-t border-border/40 bg-muted/10 flex items-center justify-between gap-3">
          <div className="hidden sm:flex items-center gap-2 text-muted-foreground/40">
            <Icon name="Fingerprint" size={12} />
            <span className="text-[10px] font-bold uppercase tracking-tighter">
              CCO_MAGAZZINO | EAN: {formData.ean}
            </span>
          </div>

          <div className="flex gap-3 w-full sm:w-auto">
            {isEditing && (
              <>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-6 py-3 bg-red-500/10 text-red-500 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all border border-red-500/20 shadow-lg shadow-red-500/10 disabled:opacity-50"
                >
                  Elimina
                </button>
                <button
                  onClick={() => { setIsEditing(false); setFormData({ ...item }); setEanLocked(true); }}
                  className="px-6 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40"
                >
                  Annulla
                </button>
              </>
            )}
            <button
              onClick={isEditing ? handleSave : handleEditClick}
              disabled={loading || isCheckingAuth}
              className="flex-1 sm:flex-none px-8 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              {(loading || isCheckingAuth) ? 'Elaborazione...' : isEditing ? 'Salva Modifiche' : 'Modifica'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DettaglioModal;